<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, useAttrs, watch, type HTMLAttributes } from 'vue';
import { useFieldControlId } from './fieldContext';
import {
  addDaysIso,
  dateFieldDefaults,
  dateOrder,
  datePattern,
  parseIsoDate,
  parseTypedDate,
  parseTypedDateTime,
  toIsoDate,
  todayIso,
  type DateFieldLabels,
} from './dateField';
import Button from './Button.vue';
import IconButton from './IconButton.vue';
import Select from './Select.vue';
import LumiIcon from '../icons/LumiIcon.vue';
import { computePopoverPosition } from '../navigation/popoverPosition';

defineOptions({ inheritAttrs: false });

// Issue 508: a native `<input type="date"/"datetime-local">` renders its OWN
// visible text — segments, separators, first-day-of-week, month names — off
// the browser/OS locale. Syncing `<html lang>` (app-kit's `syncHtmlLang`) helps
// in Firefox/Safari, but Chromium, which is most of what Lumi's users run,
// ignores `lang` for this entirely and always follows the OS. That is most of
// what users actually see, so this wraps the native control behind a DISPLAY
// the suite formats itself with `Intl.DateTimeFormat`, in the caller's own
// locale — while keeping the native control underneath for what only the
// browser can do well: the picker UI, keyboard date-segment editing, and
// mobile's native date UI.
//
// `locale` is a plain string, exactly like Sidebar's own `locale` prop (ADR
// 0014: this library is headless of vue-i18n, no injected i18n seam). Pass the
// app's bare i18n code (`'de'`) or — recommended, for a stable region instead
// of whatever the runtime happens to pick — app-kit's `uiLocale(locale)`; see
// its own comment for why the suite pins one.
//
// v-model is UNCHANGED: exactly the ISO string the native input already
// emits (`yyyy-MM-dd` / `yyyy-MM-ddTHH:mm`), so every consumer's existing
// payload shape, and every API it feeds, is untouched.
//
// Issue 823: the box is a TEXT field. A date is typed the way people write it —
// any separator or none (`1.4.26`, `01/04/2026`, `01042026`), the year short or
// left out — and read in the locale's order (`parseTypedDate`). A complete date
// lands in v-model as it is typed; anything shorter on blur or Enter. Text that is
// no date leaves v-model empty and marks the box invalid, so a stale value is never
// saved behind a box that shows something else. ↑/↓ move the date by a day;
// Alt+↓ opens the calendar. `anchor` is where a field leans when it is empty: the
// month its calendar opens on, and the year a short typed date is completed to.
//
// Unlike Input/Select, `class`/`style` land on the WRAPPER — the box the
// caller actually sees and sizes (a compact toolbar filter's `w-40`, say) —
// not on the native input, which is visually hidden. Every other fallthrough
// attribute (`name`, `min`, `max`, `required`, `data-testid`, …) passes straight
// through to the native input unchanged, so an existing selector or test that
// reached the native control by one of these keeps reaching the same control.
// `id` and `aria-label` name the text box instead: it is what a label and a screen
// reader have to reach now that the field is typed into.
const props = withDefaults(
  defineProps<{
    type?: 'date' | 'datetime-local';
    /** BCP-47 tag or bare language code; see the file comment. Omitted: the app's registered locale. */
    locale?: string;
    placeholder?: string;
    disabled?: boolean;
    invalid?: boolean;
    /**
     * Accessible name AND visible label of the clear ("x") affordance.
     * Omitted: no clear control renders — a field the caller never wants
     * emptied via the UI (participants' proposal rows, say) stays exactly as
     * native inputs already behave. Given: it shows once a value exists,
     * mirroring the small clear affordance Chromium draws inside a native
     * date input by default — which this component's hidden native input no
     * longer surfaces on its own.
     */
    clearLabel?: string;
    /**
     * `false` drops the built-in calendar icon and leaves the end of the box free (38 px) for a
     * control the host lays over it.
     */
    calendar?: boolean;
    /**
     * The month/year calendar (issue 783, formerly People's own DateInput, issue 119): given
     * its words, a `date` field's calendar icon becomes a button of its own that opens a month
     * grid with month and year as direct selections — a birth date decades back is one choice,
     * not one arrow click per month. The box itself still opens the browser's picker and takes
     * typing, as without it. Ignored for `datetime-local`. Omitted: the app's registered
     * words (issue 823), so every date field of an app that registered them has it.
     */
    jump?: DateFieldLabels;
    /**
     * Where the field leans while it is empty (issue 823), as `yyyy-mm-dd`: the month the
     * calendar opens on and the year a typed `1.4.` or `1.4.29` is completed to. A birth
     * date leans 14 years back, an end date on its start. Today when omitted.
     */
    anchor?: string;
  }>(),
  { type: 'date', locale: undefined, placeholder: '', disabled: false, invalid: false, clearLabel: undefined, calendar: true, jump: undefined, anchor: undefined },
);

const registered = dateFieldDefaults();
const effectiveLocale = computed(() => props.locale || registered?.locale() || 'de');
// A host's own words win; whatever it leaves out (the typing hint's letters, say) comes from the app's.
const labels = computed<DateFieldLabels | undefined>(() => (props.jump ? { ...registered?.labels(), ...props.jump } : registered?.labels()));

const model = defineModel<string>({ default: '' });

const attrs = useAttrs();
// class/style: the wrapper. Everything else: the native input.
const rootAttrs = computed<HTMLAttributes>(() => ({ class: attrs.class as HTMLAttributes['class'], style: attrs.style as HTMLAttributes['style'] }));
const controlId = useFieldControlId(() => attrs.id as string | undefined);
const inputAttrs = computed(() => {
  const { class: _class, style: _style, id: _id, 'aria-label': _label, ...rest } = attrs;
  return rest;
});

const nativeInputEl = ref<InstanceType<typeof HTMLInputElement> | null>(null);
const textEl = ref<HTMLInputElement | null>(null);

function onNativeInput(event: Event) {
  // Read straight off the element: the native control already refuses
  // anything that is not a real calendar date/time, so this only ever sees
  // what IT produced (or an empty string) — never reformatted.
  model.value = (event.target as HTMLInputElement).value;
}

// Explicit numeric fields, not the `dateStyle`/`timeStyle` shorthands: those
// default to a 2-digit year ("17.09.26"), and the suite's existing
// `toLocaleDateString` call sites (externalPostings.ts) already settled on
// spelling the year out.
const formatter = computed(() => {
  // `timeZone: 'UTC'` is load-bearing, not a default: both `date` and
  // `datetime-local` values are "floating" wall-clock digits with no zone of
  // their own, encoded as UTC below (see `formatIso`) purely to get a `Date`
  // Intl can format. Formatting in the RUNTIME's zone instead would shift
  // those digits for anyone not sitting in UTC+0.
  const fields: Intl.DateTimeFormatOptions =
    props.type === 'datetime-local'
      ? { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }
      : { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' };
  return new Intl.DateTimeFormat(effectiveLocale.value, fields);
});

// Parsed from the ISO string's OWN digits, like the month/year calendar — never
// `new Date(iso)` for the date-only case, which reads the string as UTC
// midnight and then renders that instant in the LOCAL zone, shifting the
// calendar day for anyone east/west of UTC.
function formatIso(value: string | undefined): string {
  if (!value) return '';
  const parts = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(value);
  if (!parts) return '';
  const [, y, mo, d, h, mi] = parts;
  const asUtc = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h || 0), Number(mi || 0)));
  return formatter.value.format(asUtc);
}

// --- Typing (issue 823) ---
const minIso = computed(() => (typeof attrs.min === 'string' ? attrs.min.slice(0, 10) : undefined));
const maxIso = computed(() => (typeof attrs.max === 'string' ? attrs.max.slice(0, 10) : undefined));
const reference = computed(() => (parseIsoDate(props.anchor) ? props.anchor! : todayIso()));
const text = ref(formatIso(model.value));
const unreadable = ref(false);
const focused = ref(false);
// The value this field emitted itself: echoing it back must not rewrite what is being typed.
let emitted: string | undefined;

const placeholderText = computed(() => {
  if (props.placeholder) return props.placeholder;
  const words = labels.value;
  if (props.type !== 'date' || !words?.dayToken || !words.monthToken || !words.yearToken) return '';
  return datePattern(effectiveLocale.value, { day: words.dayToken, month: words.monthToken, year: words.yearToken });
});

function read(value: string): string | null {
  const ctx = { order: dateOrder(effectiveLocale.value), reference: reference.value, min: minIso.value, max: maxIso.value };
  if (props.type === 'datetime-local') return parseTypedDateTime(value, ctx, /T(\d{2}:\d{2})/.exec(model.value || '')?.[1]);
  return parseTypedDate(value, ctx);
}

// Complete enough to take while the person is still typing: a four-digit year (or an
// eight-digit block) and, for a date-time, its minutes.
function complete(value: string): boolean {
  if (!/\d{4}/.test(value)) return false;
  return props.type !== 'datetime-local' || /\d{1,2}[:.h]\d{2}\s*(uhr|h)?\s*$/i.test(value);
}

function emit(value: string) {
  emitted = value;
  model.value = value;
}

watch(model, (value) => {
  if (value === emitted) return;
  emitted = undefined;
  unreadable.value = false;
  text.value = formatIso(value);
});
watch(formatter, () => {
  if (!focused.value && !unreadable.value) text.value = formatIso(model.value);
});

function onTextInput(event: Event) {
  text.value = (event.target as HTMLInputElement).value;
  unreadable.value = false;
  if (!complete(text.value)) return;
  const value = read(text.value);
  if (value && value !== model.value) emit(value);
}

function commit() {
  const typed = text.value.trim();
  if (!typed) {
    unreadable.value = false;
    if (model.value) emit('');
    return;
  }
  const value = read(typed);
  if (!value) {
    unreadable.value = true;
    if (model.value) emit('');
    return;
  }
  unreadable.value = false;
  if (value !== model.value) emit(value);
  text.value = formatIso(value);
}

function onBlur() {
  focused.value = false;
  commit();
}

function step(days: number) {
  const current = model.value ? model.value.slice(0, 10) : '';
  const time = model.value.slice(10);
  const next = current ? addDaysIso(current, days) : reference.value;
  if (!next) return;
  const value = props.type === 'datetime-local' ? next + (time || 'T00:00') : next;
  emit(value);
  text.value = formatIso(value);
  unreadable.value = false;
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    commit();
  } else if (event.key === 'ArrowDown' && event.altKey) {
    event.preventDefault();
    openCalendar();
  } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
    event.preventDefault();
    if (text.value.trim() && !model.value) commit();
    if (!unreadable.value) step(event.key === 'ArrowUp' ? 1 : -1);
  }
}

const outOfRange = computed(() => {
  const day = model.value ? model.value.slice(0, 10) : '';
  return Boolean(day && ((minIso.value && day < minIso.value) || (maxIso.value && day > maxIso.value)));
});
const showInvalid = computed(() => props.invalid || unreadable.value || outOfRange.value);

function openPicker() {
  if (props.disabled) return;
  const el = nativeInputEl.value;
  if (!el) return;
  el.focus();
  const showPicker = (el as HTMLInputElement & { showPicker?: () => void }).showPicker;
  if (typeof showPicker === 'function') {
    try {
      showPicker.call(el);
      return;
    } catch {
      // Falls through to the click fallback below — a browser can implement
      // showPicker and still refuse in an edge case (e.g. no user-activation
      // context); that is not worth failing the interaction over.
    }
  }
  // Safari and any other browser without showPicker(): a focused native date
  // input opens its picker on click in every browser this library ships to.
  el.click();
}

function clear() {
  if (props.disabled) return;
  emit('');
  text.value = '';
  unreadable.value = false;
  textEl.value?.focus();
}

function openCalendar() {
  if (jumpOn.value) toggleJump();
  else openPicker();
}

// The month/year calendar. `min`/`max` arrive as plain attributes (they belong
// to the native input), so they are read from there.
const jumpOn = computed(() => !!labels.value && props.calendar && props.type === 'date');
const rootEl = ref<HTMLElement | null>(null);
const popoverEl = ref<HTMLElement | null>(null);
const open = ref(false);
const today = new Date();
const selected = computed(() => parseIsoDate(model.value));
const viewYear = ref(today.getFullYear());
const viewMonth = ref(today.getMonth());

const monthOptions = computed(() => {
  const fmt = new Intl.DateTimeFormat(effectiveLocale.value, { month: 'long', timeZone: 'UTC' });
  return Array.from({ length: 12 }, (_, i) => ({ value: i, label: fmt.format(new Date(Date.UTC(2021, i, 1))) }));
});

// A working lifetime back and ten years ahead unless `min`/`max` narrow it; the
// year in view is always offered, so a stored date outside the span stays reachable.
const yearOptions = computed(() => {
  const lo = Math.min(parseIsoDate(minIso.value)?.y ?? today.getFullYear() - 120, viewYear.value);
  const hi = Math.max(parseIsoDate(maxIso.value)?.y ?? today.getFullYear() + 10, viewYear.value);
  const out: Array<{ value: number; label: string }> = [];
  for (let y = hi; y >= lo; y--) out.push({ value: y, label: String(y) });
  return out;
});

const weekdayNames = computed(() => {
  const fmt = new Intl.DateTimeFormat(effectiveLocale.value, { weekday: 'short', timeZone: 'UTC' });
  // 2021-03-01 was a Monday — all the suite's locales start the week there.
  return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(Date.UTC(2021, 2, 1 + i))));
});

const cells = computed(() => {
  const first = new Date(Date.UTC(viewYear.value, viewMonth.value, 1));
  const lead = (first.getUTCDay() + 6) % 7;
  const days = new Date(Date.UTC(viewYear.value, viewMonth.value + 1, 0)).getUTCDate();
  const out: Array<{ day: number; iso: string } | null> = Array.from({ length: lead }, () => null);
  for (let d = 1; d <= days; d++) out.push({ day: d, iso: toIsoDate(viewYear.value, viewMonth.value, d) });
  return out;
});

// ISO dates compare correctly as plain strings.
function isOutOfRange(iso: string): boolean {
  return Boolean((minIso.value && iso < minIso.value) || (maxIso.value && iso > maxIso.value));
}

function shiftMonth(delta: number) {
  const m = viewMonth.value + delta;
  viewYear.value += Math.floor(m / 12);
  viewMonth.value = ((m % 12) + 12) % 12;
}

function closeJump() {
  open.value = false;
}

function toggleJump() {
  if (props.disabled) return;
  open.value = !open.value;
}

function pick(iso: string) {
  emit(iso);
  text.value = formatIso(iso);
  unreadable.value = false;
  closeJump();
  textEl.value?.focus();
}

function onDocumentMouseDown(event: MouseEvent) {
  if (rootEl.value && !rootEl.value.contains(event.target as Node)) closeJump();
}

// The calendar is `position: fixed`, placed from the field's viewport rect like
// Menu.vue's panel (issue 823): a modal body or any other ancestor that clips or
// scrolls cannot cut it off. It opens below the field, aligned to its start, flips
// above when there is no room below and slides to stay inside the viewport. It does
// not chase a scrolling ancestor: a scroll outside it, or a resize, closes it.
function placePopover() {
  const field = rootEl.value;
  const panel = popoverEl.value;
  if (!field || !panel || typeof window === 'undefined') return;
  // A transformed ancestor becomes the containing block of a fixed element, so
  // where (0,0) lands is measured, not assumed.
  panel.style.top = '0px';
  panel.style.left = '0px';
  const origin = panel.getBoundingClientRect();
  const { top, left } = computePopoverPosition(
    field.getBoundingClientRect(),
    { width: origin.width, height: origin.height },
    { width: window.innerWidth, height: window.innerHeight },
    { align: 'start' },
  );
  panel.style.top = `${top - origin.top}px`;
  panel.style.left = `${left - origin.left}px`;
}

function onOutsideScroll(event: Event) {
  if (popoverEl.value && event.target instanceof Node && popoverEl.value.contains(event.target)) return;
  closeJump();
}

function listen(on: boolean) {
  if (on) {
    document.addEventListener('mousedown', onDocumentMouseDown);
    window.addEventListener('scroll', onOutsideScroll, true);
    window.addEventListener('resize', closeJump);
  } else {
    document.removeEventListener('mousedown', onDocumentMouseDown);
    window.removeEventListener('scroll', onOutsideScroll, true);
    window.removeEventListener('resize', closeJump);
  }
}

// Opening lands on the stored date (or the anchor, or today), never wherever the
// last browse ended; the listeners live only while the calendar is open.
watch(open, async (isOpen) => {
  if (isOpen) {
    const start = selected.value ?? parseIsoDate(reference.value);
    viewYear.value = start?.y ?? today.getFullYear();
    viewMonth.value = start?.m ?? today.getMonth();
    listen(true);
    await nextTick();
    placePopover();
  } else {
    listen(false);
  }
});
// Another month can have one week row more or less; a calendar sitting above the field
// keeps its bottom edge there.
watch([viewYear, viewMonth], async () => {
  if (!open.value) return;
  await nextTick();
  placePopover();
});

onBeforeUnmount(() => listen(false));
</script>

<template>
  <div
    ref="rootEl"
    class="lumi-datefield"
    :class="{
      'lumi-datefield--invalid': showInvalid,
      'lumi-datefield--disabled': disabled,
      'lumi-datefield--hostcal': !calendar || jumpOn,
      'lumi-datefield--clearable': clearLabel && model && !disabled,
    }"
    v-bind="rootAttrs"
    @keydown.esc="closeJump"
  >
    <input
      :id="controlId"
      ref="textEl"
      type="text"
      class="lumi-datefield__display"
      :value="text"
      :placeholder="placeholderText"
      :disabled="disabled"
      :aria-label="(attrs['aria-label'] as string | undefined)"
      :aria-invalid="showInvalid || undefined"
      :aria-required="attrs.required !== undefined && attrs.required !== false ? 'true' : undefined"
      autocomplete="off"
      spellcheck="false"
      data-lumi-datefield-text
      @input="onTextInput"
      @focus="focused = true"
      @blur="onBlur"
      @keydown="onKeydown"
    />
    <span v-if="calendar && !jumpOn" class="lumi-datefield__calendar-hit" aria-hidden="true" @click="openPicker">
      <LumiIcon name="calendar" class="lumi-datefield__calendar" stroke-width="1.8" />
    </span>
    <button
      v-if="clearLabel && model && !disabled"
      type="button"
      class="lumi-datefield__clear"
      :aria-label="clearLabel"
      :title="clearLabel"
      @click="clear"
    >
      <LumiIcon name="remove" stroke-width="3" />
    </button>
    <template v-if="jumpOn && labels">
      <IconButton
        variant="ghost"
        size="sm"
        class="lumi-datefield__toggle"
        :label="labels.open"
        :aria-expanded="open"
        :disabled="disabled"
        data-testid="datefield-calendar"
        @click="toggleJump"
      >
        <LumiIcon name="calendar" stroke-width="1.8" />
      </IconButton>
      <div v-if="open" ref="popoverEl" class="lumi-datefield__popover" role="dialog" :aria-label="labels.open">
        <div class="lumi-datefield__nav">
          <IconButton variant="ghost" size="sm" :label="labels.previousMonth" @click="shiftMonth(-1)">
            <LumiIcon name="previous" stroke-width="1.8" />
          </IconButton>
          <div class="lumi-datefield__month">
            <Select v-model="viewMonth" :options="monthOptions" :aria-label="labels.month" />
          </div>
          <div class="lumi-datefield__year">
            <Select v-model="viewYear" :options="yearOptions" :aria-label="labels.year" />
          </div>
          <IconButton variant="ghost" size="sm" :label="labels.nextMonth" @click="shiftMonth(1)">
            <LumiIcon name="next" stroke-width="1.8" />
          </IconButton>
        </div>
        <div class="lumi-datefield__weekdays">
          <span v-for="(name, i) in weekdayNames" :key="i">{{ name }}</span>
        </div>
        <div class="lumi-datefield__days">
          <template v-for="(cell, i) in cells" :key="i">
            <span v-if="!cell" />
            <button
              v-else
              type="button"
              class="lumi-datefield__day"
              :class="{ 'lumi-datefield__day--selected': cell.iso === model }"
              :disabled="isOutOfRange(cell.iso)"
              :aria-current="cell.iso === model ? 'date' : undefined"
              @click="pick(cell.iso)"
            >
              {{ cell.day }}
            </button>
          </template>
        </div>
        <Button variant="ghost" size="sm" block class="lumi-datefield__jumpclear" @click="pick('')">{{ labels.clear }}</Button>
      </div>
    </template>
    <!-- The native control stays underneath (issue 508): the browser's picker
         without the month/year calendar, form semantics (`name`, `required`), and
         every selector that sets a date on it directly. It is out of the Tab
         sequence and the accessibility tree — the text box above is the field's
         one keyboard, pointer and screen-reader entry point. -->
    <input
      ref="nativeInputEl"
      v-bind="inputAttrs"
      :type="type"
      :disabled="disabled"
      tabindex="-1"
      aria-hidden="true"
      class="lumi-datefield__native"
      :value="model"
      @input="onNativeInput"
    />
  </div>
</template>

<style>
.lumi-datefield{position:relative;display:block;width:100%;height:var(--field-h);}
.lumi-datefield__display{display:block;box-sizing:border-box;width:100%;height:100%;padding:0 38px 0 12px;border:1px solid var(--field-border);border-radius:var(--radius-md);background:var(--field-bg);color:var(--text-heading);font-family:var(--font-body);font-size:var(--text-sm);line-height:1.5;text-align:left;cursor:text;transition:border-color var(--dur-fast);}
.lumi-datefield__display::placeholder{color:var(--text-tertiary);}
.lumi-datefield__display:focus-visible{outline:2px solid var(--panel-edge);outline-offset:1px;}
.lumi-datefield--clearable .lumi-datefield__display{padding-right:60px;}
.lumi-datefield__calendar-hit{position:absolute;right:12px;top:50%;transform:translateY(-50%);display:inline-flex;cursor:pointer;}
.lumi-datefield__calendar{flex-shrink:0;width:16px;height:16px;color:var(--text-tertiary);}
.lumi-datefield--invalid .lumi-datefield__display{border-color:var(--danger);}
.lumi-datefield--disabled .lumi-datefield__display{background:var(--surface-wash);color:var(--text-secondary);cursor:not-allowed;}
.lumi-datefield--disabled .lumi-datefield__calendar-hit{pointer-events:none;}
.lumi-datefield--disabled .lumi-datefield__calendar{color:var(--text-tertiary);}
.lumi-datefield__clear{position:absolute;right:32px;top:50%;transform:translateY(-50%);display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;padding:0;border:0;border-radius:var(--radius-sm);background:transparent;color:var(--text-secondary);cursor:pointer;}
.lumi-datefield__clear:hover{background:var(--surface-wash);color:var(--text-heading);}
.lumi-datefield__clear svg{width:12px;height:12px;}
.lumi-datefield__native{position:absolute;inset:0;width:100%;height:100%;opacity:0;pointer-events:none;border:0;padding:0;margin:0;}
/* The month/year calendar (`jump`): its button sits in the 38 px the box keeps
   free at its end; the grid floats, fixed, on the popover layer (placePopover). */
.lumi-datefield .lumi-datefield__toggle{position:absolute;right:4px;top:50%;transform:translateY(-50%);}
.lumi-datefield__toggle svg{width:16px;height:16px;}
.lumi-datefield__popover{position:fixed;top:0;left:0;z-index:var(--layer-popover);width:20rem;max-width:calc(100vw - 16px);box-sizing:border-box;padding:var(--space-3);background:var(--surface-card);border:1px solid var(--panel-edge);border-radius:var(--radius-lg);box-shadow:var(--shadow-lg);font-family:var(--font-body);}
.lumi-datefield__nav{display:flex;align-items:center;gap:4px;}
.lumi-datefield__nav svg{width:16px;height:16px;}
.lumi-datefield__month{flex:1;min-width:0;}
.lumi-datefield__year{width:6rem;}
.lumi-datefield__weekdays{display:grid;grid-template-columns:repeat(7,1fr);margin-top:var(--space-2);text-align:center;font-size:var(--text-xs);color:var(--text-tertiary);}
.lumi-datefield__weekdays span{padding:4px 0;}
.lumi-datefield__days{display:grid;grid-template-columns:repeat(7,1fr);gap:2px;}
.lumi-datefield__day{padding:4px 0;background:none;border:0;border-radius:var(--radius-sm);font:inherit;font-size:var(--text-sm);color:var(--text-body);cursor:pointer;}
.lumi-datefield__day:hover:not(:disabled){background:var(--surface-wash);}
.lumi-datefield__day:disabled{color:var(--text-tertiary);opacity:.5;cursor:not-allowed;}
.lumi-datefield__day--selected,.lumi-datefield__day--selected:hover:not(:disabled){background:var(--btn-primary-bg);color:var(--btn-primary-fg);}
.lumi-datefield__popover .lumi-datefield__jumpclear{margin-top:var(--space-2);}
</style>
